import { ReactNode, useState } from "react";
import {
  QueryCache,
  QueryClient,
  QueryClientProvider as LibQueryClientProvider,
} from "@tanstack/react-query";

export type QueryClientProviderProps = {
  children?: ReactNode;
  queryCache?: QueryCache;
};

/**
 * Query client provider which should generally appear in an apps component
 * tree before using react-query features.
 *
 * @see {@link queries/hooks!useFetchQuery} and {@link queries/hooks!useFetchMutation}
 *   for how to consume in general application code.
 * */
const QueryClientProvider = ({ children, queryCache }: QueryClientProviderProps) => {
  /**
   * queryClient for application queries and mutations, all application consumers should access the query client
   * through `useQueryClient` and not reference this variable directly.
   */
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // Always fetch queries even if the network is offline to avoid the browser giving false positives
            // see: https://github.com/TanStack/query/issues/1657#issuecomment-2099628081
            networkMode: "always",
            // Queries will only be stale and refetched if marked as invalidated manually or after this duration in MS (5 minutes)
            staleTime: 5 * 1000 * 60,
            // Queries will no longer be cached (and will then be garbage collected) after this duration in MS (10 minutes)
            cacheTime: 10 * 1000 * 60,
            // Don't retry automatically
            retry: false,
            // Don't refetch when focusing the window
            refetchOnWindowFocus: false,
          },
          mutations: {
            // Always fetch queries even if the network is offline to avoid the browser giving false positives
            // see: https://github.com/TanStack/query/issues/1657#issuecomment-2099628081
            networkMode: "always",
          },
        },
        // Logger is being removed in v5, but we'll remove the calls now in v4 to avoid the console
        // spam for non actionable errors in production
        // https://tanstack.com/query/v4/docs/framework/react/guides/custom-logger
        // https://vetcove.sentry.io/issues/6691930158/?t_main=errors
        logger: {
          log: () => {},
          warn: () => {},
          error: () => {},
        },
        queryCache,
      }),
  );

  return <LibQueryClientProvider client={queryClient}>{children}</LibQueryClientProvider>;
};

export default QueryClientProvider;
